import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { DatePicker } from "antd";
import { Spin } from "antd";
import "antd/dist/reset.css";
import { getRequest } from "../service/index";
import { getEmployeeId } from '../utils/localStorage';
import { toDateKey, dayjs, getUserIanaTimeZone, nowInUserTz } from '../utils/dateTime';

const { RangePicker } = DatePicker;

const MAX_RANGE_DAYS = 18;

const getYAxisConfig = (total) => {
  if (total <= 0) return { max: undefined, stepSize: 1 };

  if (total <= 10) {
    return { max: total + 1, stepSize: 1 };
  }

  const paddingStep = 30;
  const tickStep = 10;
  const max = Math.ceil((total + paddingStep) / tickStep) * tickStep;
  return { max, stepSize: tickStep };
};

const isAttended = (log) => {
  const status = (log.status || '').trim().toLowerCase();
  return status && status !== 'absent' && status !== 'missing';
};

const AdminChart = () => {
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [attendanceLog, setAttendanceLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    nowInUserTz().subtract(MAX_RANGE_DAYS, 'day').startOf('day'),
    nowInUserTz().startOf('day'),
  ]);

  // Normalize logs: one record per employee per calendar day (latest update wins)
  const getNormalizedLogs = () => {
    const byEmployeeAndDate = {};

    attendanceLog.forEach((log) => {
      const dateKey = toDateKey(log.date);
      if (!dateKey || !log.employeeId) return;

      const key = `${dateKey}-${log.employeeId}`;
      if (!byEmployeeAndDate[key]) {
        byEmployeeAndDate[key] = log;
        return;
      }

      const prev = byEmployeeAndDate[key];
      const prevTime = new Date(prev.updatedAt || prev.createdAt || prev.date).getTime();
      const currTime = new Date(log.updatedAt || log.createdAt || log.date).getTime();

      if (currTime >= prevTime) {
        byEmployeeAndDate[key] = log;
      }
    });

    return Object.values(byEmployeeAndDate);
  };

  // Unique employees who actually attended per calendar day (user timezone)
  const getDateCountMap = () => {
    const dateCountMap = {};
    const normalizedLogs = getNormalizedLogs();

    normalizedLogs.forEach((log) => {
      const dateKey = toDateKey(log.date);
      if (!dateKey || !log.employeeId || !isAttended(log)) return;

      if (!dateCountMap[dateKey]) {
        dateCountMap[dateKey] = new Set();
      }
      dateCountMap[dateKey].add(log.employeeId);
    });

    const result = {};
    Object.entries(dateCountMap).forEach(([dateKey, employeeIds]) => {
      result[dateKey] = { present: employeeIds.size };
    });

    return result;
  };

  const generateAttendanceData = (rangeStart, rangeEnd) => {
    const data = [];
    const dateCountMap = getDateCountMap();
    const tz = getUserIanaTimeZone();
    let current = rangeStart.tz(tz).startOf('day');
    const end = rangeEnd.tz(tz).startOf('day');
    const safeTotal = Math.max(totalEmployees, 0);

    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const dateKey = current.format('YYYY-MM-DD');
      const presentRaw = dateCountMap[dateKey]?.present || 0;
      const presentEmployees = Math.min(presentRaw, safeTotal);
      const absentEmployees = Math.max(safeTotal - presentEmployees, 0);

      data.push({
        date: dateKey,
        dayName: current.format('ddd'),
        dayNumber: current.date(),
        monthName: current.format('MMM'),
        presentEmployees,
        absentEmployees,
        totalEmployees: safeTotal,
      });

      current = current.add(1, 'day');
    }

    return data;
  };

  const fetchAttendanceLog = async (rangeStart, rangeEnd) => {
    setLoading(true);
    try {
      const companyId = getEmployeeId();
      const startDateStr = rangeStart.format('YYYY-MM-DD');
      const endDateStr = rangeEnd.format('YYYY-MM-DD');
      
      const response = await getRequest(`/api/myattendance/byCompany?companyId=${companyId}&startDate=${startDateStr}&endDate=${endDateStr}`);
      if (response && response.attendanceLogs) {
        setAttendanceLog(response.attendanceLogs);
      } else {
        setAttendanceLog([]);
      }
    } catch (error) {
      console.error('Error fetching attendance log:', error);
      setAttendanceLog([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const daysDiff = end.diff(start, 'day');

      if (daysDiff > MAX_RANGE_DAYS) {
        const newEnd = start.add(MAX_RANGE_DAYS, 'day');
        const newDateRange = [start, newEnd];
        setDateRange(newDateRange);
        fetchAttendanceLog(start, newEnd);
      } else {
        setDateRange(dates);
        fetchAttendanceLog(start, end);
      }
    }
  };

  useEffect(() => {
    const fetchTotalEmployees = async () => {
      try {
        const [employeesResponse, reportToResponse] = await Promise.all([
          getRequest('/api/employees?pageNumber=1&pageSize=1&status=active'),
          getRequest('/api/companies/reportto'),
        ]);

        const countFromEmployees =
          employeesResponse?.pagination?.totalCount ??
          employeesResponse?.totalCount ??
          employeesResponse?.employees?.length ??
          0;
        const countFromReportTo = reportToResponse?.employees?.length ?? 0;

        setTotalEmployees(Math.max(countFromEmployees, countFromReportTo));
      } catch (error) {
        console.error('Error fetching employee count:', error);
      }
    };

    fetchTotalEmployees();
    fetchAttendanceLog(dateRange[0], dateRange[1]);
  }, []);

  const attendanceData = generateAttendanceData(dateRange[0], dateRange[1]);
  const MIN_BAR_LENGTH = 6;
  const yAxisConfig = getYAxisConfig(totalEmployees);

  // Chart data configuration
  const chartData = {
    labels: attendanceData.map(day => `${day.dayNumber} ${day.monthName}`),
    datasets: [
      {
        label: "Present Employees",
        data: attendanceData.map(day => day.presentEmployees),
        backgroundColor: attendanceData.map(day => "#231564"),
        borderColor: attendanceData.map(day => "#231564"),
        borderWidth: 1,
        borderRadius: 4,
        minBarLength: MIN_BAR_LENGTH,
      },
      {
        label: "Absent Employees",
        data: attendanceData.map(day => day.absentEmployees),
        backgroundColor: attendanceData.map(day => "#f5222d"),
        borderColor: attendanceData.map(day => "#f5222d"),
        borderWidth: 1,
        borderRadius: 4,
        minBarLength: MIN_BAR_LENGTH,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false
        }
      },
      y: {
        stacked: false,
        beginAtZero: true,
        max: yAxisConfig.max,
        ticks: {
          stepSize: yAxisConfig.stepSize,
          callback: function (value) {
            return value.toLocaleString();
          }
        },
        grid: {
          color: "#f0f0f0"
        }
      },
    },
    plugins: {
      legend: {
        display: false, // Hide the chart legend
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            const dataIndex = context[0].dataIndex;
            const day = attendanceData[dataIndex];
            return `${day.dayName}, ${day.dayNumber} ${day.monthName}`;
          },
          label: function(context) {
            const dataIndex = context.dataIndex;
            const day = attendanceData[dataIndex];
            const label = context.dataset.label || '';
            const value = label.includes('Present')
              ? day.presentEmployees
              : day.absentEmployees;
            const percentage = totalEmployees > 0 ? ((value / totalEmployees) * 100).toFixed(1) : 0;
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      bar: {
        categoryPercentage: 0.8,
        barPercentage: 0.9,
        minBarLength: MIN_BAR_LENGTH,
      }
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        {/* Legend on the left */}
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <div style={{ marginRight: "10px", display: "flex", alignItems: "center" }}>
            <div style={{ width: "30px", height: "15px", backgroundColor: "#231564", marginRight: "5px" }}></div>
            <span>Present</span>
          </div>
          <div style={{ marginRight: "10px", display: "flex", alignItems: "center" }}>
            <div style={{ width: "30px", height: "15px", backgroundColor: "#f5222d", marginRight: "5px" }}></div>
            <span>Absent</span>
          </div>
        </div>

        {/* Date Range Picker on the right */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "10px", fontSize: "14px" }}>Date Range:</span>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            style={{ width: "250px" }}
            disabledDate={(current) => {
              if (dateRange[0] && !dateRange[1]) {
                const startDate = dateRange[0];
                const maxDate = startDate.add(MAX_RANGE_DAYS, 'day');
                const minDate = startDate.subtract(MAX_RANGE_DAYS, 'day');
                return current && (current > maxDate || current < minDate);
              }
              return false;
            }}
          />
        </div>
      </div>
      <div style={{ height: "300px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default AdminChart;
